import { defineStore } from "pinia";
import { ref } from "vue";

import * as notificationsService from "@/services/notifications.service";

const POLLING_INTERVAL_MS = 120_000;

export const useNotificationsStore = defineStore("notifications", () => {
  const statusCards = ref([]);
  const unreadCount = ref(0);
  const notifications = ref([]);
  const currentPage = ref(-1);
  const hasMore = ref(false);
  const isLoadingMore = ref(false);
  const isLoadingSummary = ref(false);

  let pollingIntervalId = null;
  let visibilityHandler = null;

  async function fetchSummary() {
    isLoadingSummary.value = true;
    try {
      const { data } = await notificationsService.fetchSummary();
      statusCards.value = data?.statusCards || [];
      unreadCount.value = data?.unreadCount || 0;
    } finally {
      isLoadingSummary.value = false;
    }
  }

  async function fetchNotificationsPage(page = 0) {
    const { data } = await notificationsService.fetchNotifications(page, 5);
    const content = data?.content || [];

    if (page === 0) {
      notifications.value = content;
    } else {
      notifications.value = [...notifications.value, ...content];
    }

    currentPage.value = page;
    hasMore.value = !data?.last;
    return content;
  }

  async function loadInitialNotifications() {
    currentPage.value = -1;
    hasMore.value = false;
    await fetchNotificationsPage(0);
  }

  async function loadMoreNotifications() {
    if (!hasMore.value || isLoadingMore.value) {
      return;
    }

    isLoadingMore.value = true;
    try {
      await fetchNotificationsPage(currentPage.value + 1);
    } finally {
      isLoadingMore.value = false;
    }
  }

  async function markAllAsRead() {
    if (unreadCount.value === 0) {
      return;
    }

    const previousCount = unreadCount.value;
    unreadCount.value = 0;

    notifications.value = notifications.value.map((notification) => ({
      ...notification,
      read: true,
    }));

    try {
      await notificationsService.markAsRead();
    } catch (error) {
      unreadCount.value = previousCount;
      throw error;
    }
  }

  function startPolling() {
    stopPolling();

    pollingIntervalId = window.setInterval(() => {
      fetchSummary().catch(() => {});
    }, POLLING_INTERVAL_MS);

    visibilityHandler = () => {
      if (document.visibilityState === "visible") {
        fetchSummary().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", visibilityHandler);
  }

  function stopPolling() {
    if (pollingIntervalId !== null) {
      window.clearInterval(pollingIntervalId);
      pollingIntervalId = null;
    }

    if (visibilityHandler) {
      document.removeEventListener("visibilitychange", visibilityHandler);
      visibilityHandler = null;
    }
  }

  function resetState() {
    stopPolling();
    statusCards.value = [];
    unreadCount.value = 0;
    notifications.value = [];
    currentPage.value = -1;
    hasMore.value = false;
    isLoadingMore.value = false;
    isLoadingSummary.value = false;
  }

  return {
    statusCards,
    unreadCount,
    notifications,
    hasMore,
    isLoadingMore,
    isLoadingSummary,
    fetchSummary,
    loadInitialNotifications,
    loadMoreNotifications,
    markAllAsRead,
    startPolling,
    stopPolling,
    resetState,
  };
});
